import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { statfs } from 'node:fs/promises';
import { PrismaService } from '@cart/api-data-access';
import { DbAgentClient } from './db-agent.client';
import { BackupAlertService } from './backup-alert.service';

/**
 * A sandbox restore needs roughly this multiple of the database in free disk:
 * the restored copy itself, plus room for WAL replay and the temporary files a
 * running Postgres makes.
 */
const RESTORE_HEADROOM_MULTIPLIER = 2.5;

/** Transaction-ID wraparound is a hard stop; warn with plenty of room. */
const XID_WARNING = 150_000_000;

/**
 * Hourly database health, and the disk-headroom check that matters more than
 * it looks.
 *
 * ── WHY "CAN WE RESTORE OURSELVES?" IS A SEPARATE QUESTION FROM "IS THE DISK
 *    FULL?" ──────────────────────────────────────────────────────────────────
 *
 * The current VPS is a 2 vCPU / 4 GB / 40 GB box that also runs production
 * Postgres, six Node apps and the upload directory. The restore design puts a
 * sandbox on that same disk, and a sandbox needs ~2.5x the database free. Those
 * two thresholds cross at very different points: a disk at 60% is perfectly
 * healthy and simultaneously unable to perform the restore the whole subsystem
 * exists to guarantee.
 *
 * Discovering that during an incident is the worst possible time, so
 * `restoreHeadroomOk` is computed every hour and alerts on its own. When it
 * goes false the fix is not "free some space" — it is to move the sandbox off
 * the box, which is why `restore_jobs.sandboxHost` exists and defaults to
 * localhost rather than being hardcoded.
 */
@Injectable()
export class DatabaseHealthService {
  private readonly logger = new Logger(DatabaseHealthService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly agent: DbAgentClient,
    private readonly alerts: BackupAlertService
  ) {}

  @Cron('5 * * * *')
  async collectAll(): Promise<void> {
    let targets: Array<{ id: string; databases: string[] }>;
    try {
      targets = await this.prisma.backupTarget.findMany({
        where: { isActive: true },
        select: { id: true, databases: true },
      });
    } catch (err) {
      // Guarded for the same reason as RecoveryWindowService.recomputeAll:
      // a scheduled job that throws a stack trace before its tables exist
      // teaches people to ignore this log.
      this.logger.warn(`health collection skipped: ${(err as Error).message}`);
      return;
    }

    for (const target of targets) {
      for (const database of target.databases) {
        await this.collect(target.id, database).catch((err) =>
          this.logger.warn(`health collect failed for ${database}: ${err.message}`)
        );
      }
    }
  }

  async collect(targetId: string, databaseName: string): Promise<void> {
    const stats = await this.readStats(databaseName);
    const diskFreeBytes = await this.freeDisk();

    const restoreHeadroomOk =
      diskFreeBytes === null
        ? true
        : Number(diskFreeBytes) > Number(stats.databaseBytes) * RESTORE_HEADROOM_MULTIPLIER;

    const status =
      !restoreHeadroomOk || (stats.oldestXidAge ?? 0) > XID_WARNING
        ? 'warning'
        : stats.connectionCount > stats.maxConnections * 0.9
          ? 'warning'
          : 'healthy';

    await this.prisma.databaseHealthSnapshot.create({
      data: {
        targetId,
        databaseName,
        databaseBytes: stats.databaseBytes,
        connectionCount: stats.connectionCount,
        maxConnections: stats.maxConnections,
        cacheHitRatio: stats.cacheHitRatio,
        longestTxnSeconds: stats.longestTxnSeconds,
        deadTupleCount: stats.deadTupleCount,
        oldestXidAge: stats.oldestXidAge,
        diskFreeBytes: diskFreeBytes ?? 0n,
        restoreHeadroomOk,
        status,
      },
    });

    await this.evaluate(targetId, databaseName, {
      restoreHeadroomOk,
      diskFreeBytes,
      databaseBytes: stats.databaseBytes,
      oldestXidAge: stats.oldestXidAge,
    });

    await this.prisma.backupTarget.update({
      where: { id: targetId },
      data: { sizeBytes: stats.databaseBytes },
    });
  }

  /**
   * Read stats through the agent where it exists, and fall back to a direct
   * query where it does not.
   *
   * The fallback matters for developer machines: none of them run the agent, and
   * a health page that is simply blank locally is a page nobody develops against.
   */
  private async readStats(databaseName: string): Promise<{
    databaseBytes: bigint;
    connectionCount: number;
    maxConnections: number;
    cacheHitRatio: number | null;
    longestTxnSeconds: number | null;
    deadTupleCount: bigint;
    oldestXidAge: number | null;
  }> {
    if (this.agent.isConfigured()) {
      const result = await this.agent.call('health.collect', { database: databaseName });
      if (result.ok) {
        const [size, conns, maxConns, hit, longest, dead, xid] = result.stdout.trim().split('|');
        return {
          databaseBytes: BigInt(size || 0),
          connectionCount: Number(conns || 0),
          maxConnections: Number(maxConns || 0),
          cacheHitRatio: hit ? Number(hit) : null,
          longestTxnSeconds: longest ? Number(longest) : null,
          deadTupleCount: BigInt(dead || 0),
          oldestXidAge: xid ? Number(xid) : null,
        };
      }
    }

    const rows = await this.prisma.$queryRawUnsafe<
      Array<{ size: bigint; conns: bigint; maxconns: number; hit: number | null; dead: bigint }>
    >(
      `SELECT pg_database_size(current_database()) AS size,
              (SELECT count(*) FROM pg_stat_activity) AS conns,
              (SELECT setting::int FROM pg_settings WHERE name='max_connections') AS maxconns,
              (SELECT coalesce(sum(heap_blks_hit)::float / nullif(sum(heap_blks_hit)+sum(heap_blks_read),0),0)
                 FROM pg_statio_user_tables) AS hit,
              (SELECT coalesce(sum(n_dead_tup),0) FROM pg_stat_user_tables) AS dead`
    );

    const row = rows[0];
    return {
      databaseBytes: BigInt(row?.size ?? 0),
      connectionCount: Number(row?.conns ?? 0),
      maxConnections: Number(row?.maxconns ?? 0),
      cacheHitRatio: row?.hit ?? null,
      longestTxnSeconds: null,
      deadTupleCount: BigInt(row?.dead ?? 0),
      oldestXidAge: null,
    };
  }

  private async freeDisk(): Promise<bigint | null> {
    try {
      const stats = await statfs(process.env.PGDATA_MOUNT ?? '/');
      return BigInt(stats.bavail) * BigInt(stats.bsize);
    } catch {
      // Not available on every platform (notably Windows dev machines). A
      // missing reading is not a failure — it just means this check abstains
      // rather than raising a false alarm.
      return null;
    }
  }

  private async evaluate(
    targetId: string,
    databaseName: string,
    reading: {
      restoreHeadroomOk: boolean;
      diskFreeBytes: bigint | null;
      databaseBytes: bigint;
      oldestXidAge: number | null;
    }
  ): Promise<void> {
    if (!reading.restoreHeadroomOk) {
      await this.alerts.raise({
        kind: 'disk_low',
        severity: 'critical',
        targetId,
        title: `Not enough free disk to restore ${databaseName} on this box`,
        detail: {
          databaseBytes: reading.databaseBytes.toString(),
          diskFreeBytes: reading.diskFreeBytes?.toString() ?? 'unknown',
          neededBytes: Math.round(Number(reading.databaseBytes) * RESTORE_HEADROOM_MULTIPLIER).toString(),
          note:
            'A sandbox restore needs roughly 2.5x the database in free space. The fix is to move ' +
            'the sandbox off this host (restore_jobs.sandboxHost), not to free a little space.',
        },
      });
    } else {
      await this.alerts.resolve('disk_low', targetId);
    }

    if ((reading.oldestXidAge ?? 0) > XID_WARNING) {
      await this.alerts.raise({
        kind: 'xid_wraparound',
        severity: 'warning',
        targetId,
        title: `Transaction ID age is ${reading.oldestXidAge} on ${databaseName}`,
        detail: { oldestXidAge: reading.oldestXidAge, threshold: XID_WARNING },
      });
    } else {
      await this.alerts.resolve('xid_wraparound', targetId);
    }
  }

  /** Keep the time series bounded. 90 days at hourly resolution. */
  @Cron('50 3 * * *')
  async prune(): Promise<void> {
    const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    const removed = await this.prisma.databaseHealthSnapshot.deleteMany({
      where: { collectedAt: { lt: cutoff } },
    });
    if (removed.count) this.logger.log(`pruned ${removed.count} health snapshot(s)`);
  }
}
